// src/components/GpEfficiencyPanel.tsx (2026-08-26 新設・開発版タブ)
// GP効率分析: GP = 延数 × 1日GP単価 の分解ビュー。
//
// 背景 (2026-08-26 ユーザ指示・科別GP分析スライドの知見):
//   現行のGP分析タブは 1日GP単価だけで群分類(1〜4群)するため、
//   延数(ボリューム)で GP を稼ぐ科を過小評価する。
//   効率化の順番は ① 空床を埋める(延数を増やす) ② 1日単価を高める。
//
// 構成 (2026-08-26 ユーザ指示「1軸のシンプルな可視化から2軸の散布図へ徐々に深める」
//   +「医師1人あたりという可視化も大切」):
//   STEP1 1日GP単価ランキング   (現行の群分類はこの軸だけで決まる・閾値の縦線つき)
//   STEP2 延数ランキング        (並べ替えると順位が一変・延数上位に3群/4群)
//   STEP3 GPランキング          (=延数×単価。単価の群と順位が食い違う=過小評価の証拠)
//   STEP4 医師1人あたりGP       (医師という資源あたりの効率。分母=常勤換算数)
//   STEP5 延数×単価の散布図     (等GP曲線つき・2軸の統合)
//   STEP6 医師1人あたりの散布図 (x=医師1人あたり延数×y=単価・等「医師1人あたりGP」曲線)
// データは GP分析タブと同じ /api/query/profit (axis=dept・queryKey共有=リクエスト増なし)。
// 常勤換算数のみ /api/query/ranking (metric=常勤換算数・医療区分/予定緊急=合計) で追加取得。
import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { useFilters, PROFIT_BASIS_DISCHARGE_ENABLED } from "../store";
import { useProfitPeriod } from "../lib/useProfitPeriod";
import { PlotFigure } from "./Plot";
import { CsvButton } from "./CsvButton";
import { fmtInt } from "../lib/format";
import type { ProfitResponse, KpiResponse, RankingResponse } from "@shared/api";

const GUN_COLORS: Record<string, string> = { "1群": "#1B9E77", "2群": "#386FA4", "3群": "#D9A036", "4群": "#94a3b8" };

function gunOf(gpT: number | null, g1: number, g2: number, g3: number): string | null {
  return gpT == null ? null : gpT >= g1 ? "1群" : gpT >= g2 ? "2群" : gpT >= g3 ? "3群" : "4群";
}

const fmtOku = (v: number | null | undefined, digits = 2): string =>
  v == null || !Number.isFinite(v) ? "—" : `${(v / 1e8).toFixed(digits)}億円`;
const fmtMan = (v: number): string => `${(v / 1e4).toLocaleString("ja-JP", { maximumFractionDigits: 1 })}万`;

interface Pt { 診療科: string; x: number; y: number; gp: number; gun: string; jin: number | null; fte?: number | null; }
interface PtDr extends Pt { fte: number; gpDr: number; daysDr: number; }

const PLOT_STYLE = { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" };

function GunLegend() {
  return (
    <span className="ml-1">
      {(["1群", "2群", "3群", "4群"] as const).map((g) => (
        <span key={g} className="inline-flex items-center gap-1 ml-2">
          <span style={{ width: 9, height: 9, background: GUN_COLORS[g], display: "inline-block", borderRadius: 2 }}></span>{g}
        </span>
      ))}
    </span>
  );
}

function Step({ n, title, take, sub, children }: {
  n: number; title: string; take?: ReactNode; sub?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="card p-4">
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-[11px] font-bold text-white bg-slate-700 rounded px-1.5 py-0.5 shrink-0">STEP {n}</span>
        <h3 className="text-sm font-bold">{title}</h3>
      </div>
      {take && <div className="text-xs text-slate-700 mb-2">{take}</div>}
      {children}
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </section>
  );
}

// 横棒ランキング (色=群分類・値ラベルつき)。STEP1〜4 で共用。
function rankBarOptions<T extends Pt>(rows: T[], o: {
  value: (p: T) => number;
  label: (p: T) => string;
  xLabel: string;
  xTick: (v: number) => string;
  thresholds?: number[];
  marginRight?: number;
  tip: (p: T) => string;
}): Plot.PlotOptions {
  const sorted = [...rows].sort((a, b) => o.value(b) - o.value(a));
  return {
    width: 1100, height: sorted.length * 23 + 66, marginLeft: 112, marginRight: o.marginRight ?? 92, marginTop: 18, marginBottom: 40,
    style: PLOT_STYLE,
    x: { label: o.xLabel, grid: true, tickFormat: o.xTick },
    y: { domain: sorted.map((r) => r.診療科), label: null },
    marks: [
      Plot.barX(sorted, { x: (d: T) => o.value(d), y: "診療科", fill: (d: T) => GUN_COLORS[d.gun], fillOpacity: 0.9 }),
      ...(o.thresholds ? [Plot.ruleX(o.thresholds, { stroke: "#64748b", strokeDasharray: "3 3", strokeWidth: 1 })] : []),
      Plot.text(sorted, { x: (d: T) => o.value(d), y: "診療科", text: o.label, dx: 6, textAnchor: "start", fontSize: 10, fill: "#334155" }),
      Plot.tip(sorted, Plot.pointer({ x: (d: T) => o.value(d), y: "診療科", title: o.tip })),
    ],
  };
}

// 2軸散布図 (スライド版)。点色=現行の群分類。ラベルは候補×貪欲配置で衝突回避。
//   オプションで 等値曲線 (x·y=一定)・原点からの基準線 (傾き=病院全体値)・群分類の横帯
//   (y=1日GP単価のときのみ) を重ねられる。
function scatterOptionsFor<T extends Pt>(pts: T[], o: {
  xLabel: string;
  yLabel: string;
  xTick?: (v: number) => string;
  yTick: (v: number) => string;
  gun: [number, number, number];
  bands?: boolean;                       // y=1日GP単価 のとき true (群分類の横帯)
  sizeByFte?: boolean;                   // 円の大きさ=常勤換算医師数 (科別の散布図で使う)
  isoCandidates?: number[];
  isoText?: (l: number) => string;
  refSlope?: { slope: number; label: string } | null;   // 原点からの基準線
  focusedDept: string | null;
  tip: (p: T) => string;
}): Plot.PlotOptions {
  if (pts.length === 0) return { width: 1100, height: 100, marks: [] };
  const [g1, g2, g3] = o.gun;
  const xmax = Math.max(...pts.map((r) => r.x)) * 1.12;
  const ymax = Math.max(...pts.map((r) => r.y), ...(o.bands ? [g1 * 1.1] : [])) * 1.15;
  // 円面積∝常勤換算数 (r スケールは sqrt)。医師数が無い科は最小径で描く
  const fteOf = (p: Pt) => p.fte ?? 0.3;
  const fteMax = o.sizeByFte ? Math.max(...pts.map((p) => fteOf(p)), 1) : 1;
  const R_MIN = 3, R_MAX = 16;
  const rPx = (p: Pt) => o.sizeByFte ? R_MIN + (R_MAX - R_MIN) * Math.sqrt(fteOf(p) / fteMax) : 7;
  // ピクセル近似 (ラベル衝突回避に使う)。responsiveWidth 縮小時も相対関係は保たれる。
  const W = 1100, H = 560, ML = 70, MR = 40, MT = 24, MB = 50;
  const X2 = (x: number) => ML + (x / xmax) * (W - ML - MR);
  const Y2 = (y: number) => MT + (1 - y / ymax) * (H - MT - MB);
  type Box = { x0: number; y0: number; x1: number; y1: number };
  const ov = (a: Box, b: Box) =>
    Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) *
    Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
  const dotBoxes: Box[] = pts.map((r) => {
    const rp = rPx(r) + 1;
    return { x0: X2(r.x) - rp, y0: Y2(r.y) - rp, x1: X2(r.x) + rp, y1: Y2(r.y) + rp };
  });
  const levels = (o.isoCandidates ?? []).filter((l) => l >= xmax * ymax * 0.03 && l <= xmax * ymax * 0.75);
  const curves = levels.map((l) => {
    const x0 = Math.max(l / ymax, xmax * 0.015);
    const cpts: { x: number; y: number }[] = [];
    for (let i = 0; i <= 80; i++) {
      const x = x0 * Math.pow(xmax / x0, i / 80);
      cpts.push({ x, y: l / x });
    }
    return cpts;
  });
  // 等値曲線ラベル: 曲線上の候補位置 (右端→内側) から点と重ならない場所を選ぶ
  const isoRight: { x: number; y: number; text: string }[] = [];
  const isoTop: { x: number; y: number; text: string }[] = [];
  const isoBoxes: Box[] = [];
  for (const l of levels) {
    const text = (o.isoText ?? String)(l);
    const w = 10 * text.length + 4;
    let done = false;
    for (const fx of [0.99, 0.87, 0.74, 0.62]) {
      const y = l / (xmax * fx);
      if (y > ymax * 0.9 || y < ymax * 0.03) continue;
      const box: Box = { x0: X2(xmax * fx) - w, y0: Y2(y) - 19, x1: X2(xmax * fx), y1: Y2(y) - 1 };
      if (!dotBoxes.some((d) => ov(box, d) > 0) && !isoBoxes.some((b) => ov(box, b) > 0)) {
        isoRight.push({ x: xmax * fx, y, text });
        isoBoxes.push(box);
        done = true;
        break;
      }
    }
    if (!done) {
      const yAtRight = l / xmax;
      if (yAtRight > ymax * 0.05) {
        isoRight.push({ x: xmax * 0.99, y: yAtRight, text });
        isoBoxes.push({ x0: X2(xmax * 0.99) - w, y0: Y2(yAtRight) - 19, x1: X2(xmax * 0.99), y1: Y2(yAtRight) - 1 });
      } else {
        isoTop.push({ x: l / (ymax * 0.93), y: ymax * 0.93, text });
        isoBoxes.push({ x0: X2(l / (ymax * 0.93)), y0: Y2(ymax * 0.93) - 19, x1: X2(l / (ymax * 0.93)) + w, y1: Y2(ymax * 0.93) - 1 });
      }
    }
  }
  const bands = o.bands ? [
    { y1: g1, y2: ymax, g: "1群" },
    { y1: g2, y2: g1, g: "2群" },
    { y1: g3, y2: g2, g: "3群" },
    { y1: 0, y2: g3, g: "4群" },
  ] : [];
  // 原点からの基準線 (傾き=病院全体値)。ラベルは線の終端近くに置き、回避対象に登録する。
  let refMarks: Plot.Markish[] = [];
  if (o.refSlope && o.refSlope.slope > 0) {
    const ex = Math.min(xmax, ymax / o.refSlope.slope);
    const lx = ex * 0.97, ly = lx * o.refSlope.slope;
    const w = 10 * o.refSlope.label.length + 4;
    isoBoxes.push({ x0: X2(lx) - w, y0: Y2(ly) - 22, x1: X2(lx), y1: Y2(ly) - 4 });
    refMarks = [
      Plot.line([{ x: 0, y: 0 }, { x: ex, y: ex * o.refSlope.slope }],
        { x: "x", y: "y", stroke: "#E8743B", strokeWidth: 1.5, strokeDasharray: "6 4" }),
      Plot.text([{ x: lx, y: ly, text: o.refSlope.label }],
        { x: "x", y: "y", text: "text", fontSize: 10, fontWeight: "bold", fill: "#E8743B", textAnchor: "end", dy: -9 }),
    ];
  }
  // 円サイズの凡例 (プロット左上・科名ラベルの回避対象に登録)
  const sizeNote = o.sizeByFte ? "円の大きさ=常勤換算医師数" : null;
  if (sizeNote) isoBoxes.push({ x0: X2(xmax * 0.005), y0: Y2(ymax * 0.99), x1: X2(xmax * 0.005) + 10 * sizeNote.length + 4, y1: Y2(ymax * 0.99) + 16 });
  return {
    width: 1100, height: 560, marginLeft: 70, marginRight: 40, marginTop: 24, marginBottom: 50,
    style: PLOT_STYLE,
    x: { label: `${o.xLabel} →`, grid: true, tickFormat: o.xTick ?? ((v: number) => fmtInt(v)), domain: [0, xmax] },
    y: { label: `↑ ${o.yLabel}`, grid: true, tickFormat: o.yTick, domain: [0, ymax] },
    ...(o.sizeByFte ? { r: { type: "identity" as const } } : {}),
    marks: [
      ...(sizeNote ? [Plot.text([{ x: xmax * 0.005, y: ymax * 0.99, text: sizeNote }],
        { x: "x", y: "y", text: "text", fontSize: 10, fill: "#94a3b8", textAnchor: "start" })] : []),
      // 現行の群分類 = 横帯 (単価だけで切っている、を可視化。y=1日GP単価のときのみ)
      ...bands.map((b) => Plot.rect([{}], { x1: 0, x2: xmax, y1: b.y1, y2: b.y2, fill: GUN_COLORS[b.g], fillOpacity: 0.045 })),
      ...bands.map((b) => Plot.text([{ x: xmax * 0.005, y: Math.min((b.y1 + b.y2) / 2, ymax * 0.985), text: b.g }],
        { x: "x", y: "y", text: "text", fontSize: 10, fontWeight: "bold", fill: GUN_COLORS[b.g], textAnchor: "start", opacity: 0.85 })),
      ...(o.bands ? [Plot.ruleY([g1, g2, g3], { stroke: "#94a3b8", strokeDasharray: "2 3", strokeWidth: 0.8 })] : []),
      // 原点からの基準線 (病院全体の比率)
      ...refMarks,
      // 等値曲線 (同じ曲線上 = x·y が同じ)
      ...curves.map((cpts) => Plot.line(cpts, { x: "x", y: "y", stroke: "#0f766e", strokeWidth: 1.2, strokeDasharray: "6 3", opacity: 0.55 })),
      Plot.text(isoRight, { x: "x", y: "y", text: "text", fontSize: 10, fill: "#0f766e", textAnchor: "end", dy: -6, opacity: 0.9 }),
      Plot.text(isoTop, { x: "x", y: "y", text: "text", fontSize: 10, fill: "#0f766e", textAnchor: "start", dy: -6, opacity: 0.9 }),
      // 科のドット (色=現行の群分類。sizeByFte なら円面積∝常勤換算数・大きい順=小円が前面)
      Plot.dot(o.sizeByFte ? [...pts].sort((a, b) => fteOf(b) - fteOf(a)) : pts, {
        x: "x", y: "y",
        ...(o.sizeByFte ? { r: (d: T) => rPx(d) } : { r: 6 }),
        fill: (d: T) => GUN_COLORS[d.gun],
        fillOpacity: 0.85,
        stroke: (d: T) => o.focusedDept && d.診療科 === o.focusedDept ? "#dc2626" : "#fff",
        strokeWidth: (d: T) => o.focusedDept && d.診療科 === o.focusedDept ? 3 : 1,
      }),
      // 科名ラベル: 候補位置×貪欲配置の簡易衝突回避 (ピクセル近似)。
      //   dx/dy/textAnchor は Plot.text の定数オプションなので、候補ごとにマークを分ける。
      //   等値曲線ラベル (isoBoxes) も回避対象に含める。
      ...(() => {
        const CANDS = [
          { anchor: "middle", dx: 0, dy: -11 }, { anchor: "middle", dx: 0, dy: 16 },
          { anchor: "start", dx: 9, dy: -8 }, { anchor: "end", dx: -9, dy: -8 },
          { anchor: "start", dx: 9, dy: 12 }, { anchor: "end", dx: -9, dy: 12 },
          { anchor: "middle", dx: 0, dy: -24 }, { anchor: "middle", dx: 0, dy: 29 },
        ] as const;
        const boxFor = (p: T, c: (typeof CANDS)[number]): Box => {
          const w = 10 * p.診療科.length + 4, h = 13;
          const cx = X2(p.x) + c.dx, cy = Y2(p.y) + c.dy;
          const x0 = c.anchor === "middle" ? cx - w / 2 : c.anchor === "start" ? cx : cx - w;
          return { x0, y0: cy - h / 2, x1: x0 + w, y1: cy + h / 2 };
        };
        const placedBoxes: Box[] = [...isoBoxes];
        const groups: T[][] = CANDS.map(() => []);
        for (const p of [...pts].sort((a, b) => Y2(a.y) - Y2(b.y))) {
          let best = 0, bestPen = Infinity;
          CANDS.forEach((c, i) => {
            const b = boxFor(p, c);
            let pen = i * 0.5;                                    // 既定は上を優先
            for (const q of placedBoxes) pen += ov(b, q) * 10;    // ラベル同士・等値ラベルは最優先で回避
            for (const d of dotBoxes) pen += ov(b, d);            // 円との重なりは軽い減点
            if (b.x0 < 2 || b.x1 > W - 2 || b.y0 < 2) pen += 500; // 枠外は避ける
            if (pen < bestPen) { bestPen = pen; best = i; }
          });
          placedBoxes.push(boxFor(p, CANDS[best]));
          groups[best].push(p);
        }
        return groups.flatMap((g, i) => g.length === 0 ? [] :
          [Plot.text(g, { x: "x", y: "y", text: "診療科", fontSize: 10, dx: CANDS[i].dx, dy: CANDS[i].dy, textAnchor: CANDS[i].anchor, fill: "#222", stroke: "white", strokeWidth: 3 })]);
      })(),
      Plot.tip(pts, Plot.pointer({ x: "x", y: "y", title: o.tip })),
    ],
  };
}

export function GpEfficiencyPanel() {
  const f = useFilters();
  const period = useProfitPeriod();
  const vol = f.kpiVolumeBase === "在院" ? "在院" : "入院";
  const isDischarge = f.profitBasis === "退院";

  // GP分析タブの科別チャートと同一 queryKey (キャッシュ共有)
  const { data, isLoading } = useQuery<ProfitResponse>({
    queryKey: ["profit-chart", "dept", f.profitBasis, f.granularity, f.yotei, f.profitIryo, f.kpiVolumeBase, period.from, period.to, f.routeKeiyu],
    queryFn: () => api.profit({ axis: "dept", basis: f.profitBasis, granularity: f.granularity, yotei: f.yotei, iryo: f.profitIryo, volBase: f.kpiVolumeBase, dateFrom: period.from, dateTo: period.to, focusedDept: null, focusedDoctor: null, keiyu: f.routeKeiyu }),
    placeholderData: (p) => p,
  });

  // 病床稼働率 (病院全体・医療区分/予定緊急=合計・参考値)。空床の文脈を出すために添える。
  const { data: occ } = useQuery<KpiResponse>({
    queryKey: ["kpi", "病床稼働率", period.from, period.to],
    queryFn: () => api.kpi({ granularity: "小", iryo: "合計", yotei: "合計", metric: "病床稼働率", asOfMonth: period.to, periodFrom: period.from }),
    placeholderData: (p) => p,
    staleTime: 60_000,
  });

  // 常勤換算数 (科別・期間の月平均)。医師数は医療区分/予定緊急で割れないので常に合計で引く。
  const { data: fteData } = useQuery<RankingResponse>({
    queryKey: ["ranking", "常勤換算数", f.granularity, period.from, period.to],
    queryFn: () => api.ranking({ granularity: f.granularity, iryo: "合計", yotei: "合計", metric: "常勤換算数", dateFrom: period.from, dateTo: period.to }),
    placeholderData: (p) => p,
    staleTime: 60_000,
  });

  const total = useMemo(() => (data?.rows ?? []).find((r) => r.key === "__total__") ?? null, [data]);
  const fteMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of fteData?.rows ?? []) if (r.avg != null && r.avg > 0) m[r.診療科] = r.avg;
    return m;
  }, [fteData]);

  const rows = useMemo<Pt[]>(() => {
    return (data?.rows ?? [])
      .filter((r) => r.key !== "__total__" && r.延数 > 0 && r.粗利 != null && r["1日粗利単価"] != null)
      .map((r) => ({
        診療科: r.key,
        x: r.延数,
        y: r["1日粗利単価"] as number,
        gp: r.粗利 as number,
        gun: gunOf(r["1日粗利単価"], f.gun1, f.gun2, f.gun3) as string,
        jin: r.医師人件費,
        fte: fteMap[r.key] ?? null,
      }));
  }, [data, f.gun1, f.gun2, f.gun3, fteMap]);

  // 医師1人あたり (常勤換算数>0 の科のみ。救急科(医師数0)などはここから外れる)
  const drRows = useMemo<PtDr[]>(() =>
    rows.filter((r) => r.fte != null).map((r) => ({
      ...r, fte: r.fte as number,
      gpDr: r.gp / (r.fte as number),
      daysDr: r.x / (r.fte as number),
    })), [rows]);

  // 単価順位 (STEP3 の注記「単価◯位」用)
  const unitRank = useMemo(() => {
    const m: Record<string, number> = {};
    [...rows].sort((a, b) => b.y - a.y).forEach((r, i) => { m[r.診療科] = i + 1; });
    return m;
  }, [rows]);

  // 空床の月平均 (延数 × (1−稼働率)/稼働率)。フィルタが「病院全体の入院延数」と一致する
  // クリーンな条件のときだけ数値を出す (それ以外は稼働率のみ)。稼働率≥100% の期間は出さない。
  const occAvg = occ?.periodAvg ?? null;
  const cleanForBeds = !isDischarge && f.profitIryo === "合計" && f.yotei === "合計"
    && f.routeKeiyu === "合計" && f.kpiVolumeBase === "入院";
  const emptyBeds = cleanForBeds && occAvg != null && occAvg < 1 && total
    ? total.延数 * (1 - occAvg) / occAvg
    : null;

  const tipTitle = (d: Pt) =>
    `${d.診療科} (${d.gun})\n${vol}延数: ${fmtInt(d.x)} 人日/月\n1日GP単価: ${fmtInt(d.y)} 円/日\nGP: ${fmtOku(d.gp)}/月 (=延数×単価)`
    + (d.fte != null ? `\n常勤換算: ${d.fte.toFixed(1)} 人` : "");
  const tipTitleDr = (d: PtDr) =>
    `${d.診療科} (${d.gun})\n常勤換算: ${d.fte.toFixed(1)} 人\n医師1人あたり延数: ${fmtInt(d.daysDr)} 人日/月\n1日GP単価: ${fmtInt(d.y)} 円/日\n医師1人あたりGP: ${fmtMan(d.gpDr)}円/月`;

  // ── STEP1〜4: 1軸ランキング ──────────────────────────────
  const step1 = useMemo(() => rankBarOptions(rows, {
    value: (d) => d.y,
    label: (d) => `${fmtMan(d.y)} (${d.gun})`,
    xLabel: "1日GP単価 (万円/日)", xTick: fmtMan,
    thresholds: [f.gun1, f.gun2, f.gun3],
    tip: tipTitle,
  }), [rows, f.gun1, f.gun2, f.gun3, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  const step2 = useMemo(() => rankBarOptions(rows, {
    value: (d) => d.x,
    label: (d) => `${fmtInt(d.x)} (${d.gun})`,
    xLabel: `${vol}患者延数 (人日/月)`, xTick: (v) => fmtInt(v),
    tip: tipTitle,
  }), [rows, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  const step3 = useMemo(() => rankBarOptions(rows, {
    value: (d) => d.gp,
    label: (d) => `${(d.gp / 1e8).toFixed(2)}億 (${d.gun}・単価${unitRank[d.診療科]}位)`,
    xLabel: "GP (億円/月)", xTick: (v) => (v / 1e8).toFixed(1),
    marginRight: 128,
    tip: tipTitle,
  }), [rows, unitRank, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  const step4 = useMemo(() => rankBarOptions(drRows, {
    value: (d) => d.gpDr,
    label: (d) => `${fmtMan(d.gpDr)} (${d.gun}・${d.fte.toFixed(1)}人)`,
    xLabel: "医師1人あたりGP (万円/月)", xTick: fmtMan,
    marginRight: 132,
    tip: tipTitleDr,
  }), [drRows, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  // 動的な takeaway (延数/GP 上位5科のうち3群以下が何科か)
  const lowGunIn = (key: "x" | "gp", n = 5) =>
    [...rows].sort((a, b) => b[key] - a[key]).slice(0, n)
      .filter((r) => r.gun === "3群" || r.gun === "4群").length;
  const drTop3 = useMemo(() =>
    [...drRows].sort((a, b) => b.gpDr - a.gpDr).slice(0, 3).map((r) => r.診療科).join("・"), [drRows]);

  // ── STEP5〜8: 2軸散布図 (科別GP分析スライドと同じ系列・順番) ──────
  const gunT: [number, number, number] = [f.gun1, f.gun2, f.gun3];
  const totalJinRatio = total?.医師人件費比
    ?? (total?.粗利 != null && total?.医師人件費 ? (total.粗利 as number) / (total.医師人件費 as number) : null);
  const totalUnit = total?.["1日粗利単価"] ?? null;
  const okuTick = (v: number) => (v / 1e8).toFixed(1);

  // STEP5: 医師人件費 × GP (基準線=病院全体の医師人件費比)
  const step5 = useMemo(() => scatterOptionsFor(
    rows.filter((r) => r.jin != null && r.jin > 0).map((r) => ({ ...r, x: r.jin as number, y: r.gp })), {
      xLabel: "医師人件費 (万円/月)", xTick: fmtMan,
      yLabel: "GP (億円/月)", yTick: okuTick,
      gun: gunT,
      sizeByFte: true,
      refSlope: totalJinRatio ? { slope: totalJinRatio, label: `病院全体 医師人件費比 ${totalJinRatio.toFixed(2)}倍` } : null,
      focusedDept: f.focusedDept,
      tip: (d) => `${tipTitle(d)}\n医師人件費: ${fmtMan(d.x)}円/月 (比 ${(d.gp / d.x).toFixed(2)}倍)`,
    }), [rows, f.gun1, f.gun2, f.gun3, f.focusedDept, totalJinRatio, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  // STEP6: 延数 × GP (基準線=病院全体の1日GP単価)
  const step6 = useMemo(() => scatterOptionsFor(
    rows.map((r) => ({ ...r, y: r.gp })), {
      xLabel: `${vol}患者延数 (人日/月)`,
      yLabel: "GP (億円/月)", yTick: okuTick,
      gun: gunT,
      sizeByFte: true,
      refSlope: totalUnit ? { slope: totalUnit, label: `病院全体 1日GP単価 ${fmtMan(totalUnit)}円/日` } : null,
      focusedDept: f.focusedDept,
      tip: tipTitle,
    }), [rows, f.gun1, f.gun2, f.gun3, f.focusedDept, totalUnit, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  // STEP7: 医師1人あたり延数 × 医師1人あたりGP (基準線は同じく病院全体の1日GP単価)
  const step7 = useMemo(() => scatterOptionsFor(
    drRows.map((d) => ({ ...d, x: d.daysDr, y: d.gpDr })), {
      xLabel: `医師1人あたり${vol}患者延数 (人日/月)`,
      yLabel: "医師1人あたりGP (万円/月)", yTick: fmtMan,
      gun: gunT,
      refSlope: totalUnit ? { slope: totalUnit, label: `病院全体 1日GP単価 ${fmtMan(totalUnit)}円/日` } : null,
      focusedDept: f.focusedDept,
      tip: (d) => tipTitleDr(d as PtDr),
    }), [drRows, f.gun1, f.gun2, f.gun3, f.focusedDept, totalUnit, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  // STEP8 (最後): 医師1人あたり延数 × 1日GP単価 (等・医師1人あたりGP曲線+群帯)
  const step8 = useMemo(() => scatterOptionsFor(drRows.map((d) => ({ ...d, x: d.daysDr })), {
    xLabel: `医師1人あたり${vol}患者延数 (人日/月)`,
    yLabel: "1日GP単価 (円/日)", yTick: fmtMan,
    gun: gunT,
    bands: true,
    isoCandidates: [2.5e6, 5e6, 1e7, 1.5e7, 2e7, 3e7, 4e7],
    isoText: (l) => `医師1人あたりGP ${fmtMan(l)}/月`,
    focusedDept: f.focusedDept,
    tip: (d) => tipTitleDr(d as PtDr),
  }), [drRows, f.gun1, f.gun2, f.gun3, f.focusedDept, vol]); // eslint-disable-line react-hooks/exhaustive-deps

  const ymShort = (d: string) => `${d.slice(2, 4)}年${d.slice(5, 7)}月`;
  const periodLabel = period.from === period.to ? ymShort(period.from) : `${ymShort(period.from)} 〜 ${ymShort(period.to)}`;
  const pill = "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px]";
  const thr = (v: number) => `${(v / 1e4).toLocaleString("ja-JP", { maximumFractionDigits: 1 })}万円`;
  const kpiCard = "card px-3 py-2 flex-1 min-w-[150px]";

  const empty = rows.length === 0;
  const emptyNote = (
    <div className="text-sm text-slate-400 py-12 text-center">
      この期間はGPを算出できません (医師人件費のそろわない月を含む期間など)。期間を変えてください。
    </div>
  );

  return (
    <div className="space-y-3">
      {/* 条件バー + 主張 */}
      <section className="card px-4 py-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h2 className="text-base font-bold mr-1">🛏️ GP効率分析</h2>
          <span className={`${pill} bg-purple-50 border-purple-200 text-purple-800 font-semibold`}>開発版</span>
          {/* ★2026-08-27: 退院日基準の封鎖中は「在院日基準」pill を出さない (ProfitPanel と同じ) */}
          {PROFIT_BASIS_DISCHARGE_ENABLED && (
            <span className={`${pill} ${isDischarge ? "bg-violet-50 border-violet-200 text-violet-800" : "bg-teal-50 border-teal-200 text-teal-800"} font-semibold`}>{f.profitBasis}日基準</span>
          )}
          <span className={`${pill} bg-slate-50 border-slate-200 text-slate-700 font-semibold`}>📅 {periodLabel}{period.isLatestOnly && <span className="text-[9px] text-slate-400 font-normal">最新月</span>}</span>
          <span className={`${pill} border-slate-200 bg-white text-slate-600`}>医療区分: {isDischarge ? "急性期" : f.profitIryo}</span>
          <span className={`${pill} border-slate-200 bg-white text-slate-600`}>予定緊急: {f.yotei}</span>
          <span className={`${pill} border-slate-200 bg-white text-slate-600`}>{f.granularity}分類</span>
          <span className={`${pill} border-slate-200 bg-white text-slate-600`}>{vol}延数</span>
          {isLoading && <span className="text-xs text-slate-400">loading…</span>}
          <CsvButton
            rows={drRows.map((r) => ({ 診療科: r.診療科, [`${vol}延数_人日月平均`]: r.x, "1日GP単価_円": r.y, GP_円月平均: r.gp, 群分類: r.gun, 単価順位: unitRank[r.診療科], 常勤換算数: r.fte, 医師1人あたりGP_円月平均: r.gpDr, 医師1人あたり延数_人日月平均: r.daysDr }))}
            filename={`GP効率分析_${f.granularity}_${period.from.slice(0, 7)}_${period.to.slice(0, 7)}`}
            style={{ marginLeft: "auto" }}
          />
        </div>
        <div className="text-xs text-slate-600 mt-1.5">
          GP = <b>{vol}延数 × 1日GP単価</b>。STEP 1〜4 の1軸ランキングで「単価だけの群分類が延数でGPを稼ぐ科を
          過小評価する」ことを確かめ、STEP 5〜8 の2軸散布図(スライドと同じ系列)で統合する。効率化の順番は
          <b> ① 空床を埋める(延数を増やす)</b> → <b>② 1日単価を高める</b>。
        </div>
      </section>

      {/* KPI (病院全体) */}
      <div className="flex gap-2 flex-wrap">
        <div className={kpiCard}>
          <div className="text-[10px] text-slate-500">GP (病院全体・月平均)</div>
          <div className="text-lg font-bold text-emerald-700">{fmtOku(total?.粗利)}</div>
        </div>
        <div className={kpiCard}>
          <div className="text-[10px] text-slate-500">{vol}患者延数 (月平均)</div>
          <div className="text-lg font-bold">{total ? fmtInt(total.延数) : "—"} <span className="text-xs font-normal">人日</span></div>
        </div>
        <div className={kpiCard}>
          <div className="text-[10px] text-slate-500">1日GP単価 (病院全体)</div>
          <div className="text-lg font-bold">{total ? fmtInt(total["1日粗利単価"]) : "—"} <span className="text-xs font-normal">円/日</span></div>
        </div>
        <div className={kpiCard}>
          <div className="text-[10px] text-slate-500">病床稼働率 (病院全体・期間平均・医療区分/予定緊急=合計)</div>
          <div className="text-lg font-bold">{occAvg == null ? "—" : `${(occAvg * 100).toFixed(1)} %`}
            {emptyBeds != null && emptyBeds > 0 && (
              <span className="text-xs font-normal text-amber-700 ml-2">空床 ≈ {fmtInt(emptyBeds)} 人日/月</span>
            )}
          </div>
          {occAvg != null && occAvg >= 1 && (
            <div className="text-[10px] text-slate-400">稼働率100%以上 = この期間の空床余地はほぼなし</div>
          )}
        </div>
      </div>

      {/* STEP1・STEP2: 1軸ランキングを横並び比較 */}
      <div className="grid gap-3 xl:grid-cols-2">
        <Step n={1} title="いまの見方 — 1日GP単価で並べる"
          take={<><b>現行のGP分析タブの群分類(1〜4群)は、この軸だけで決まる</b>(縦破線=閾値 {thr(f.gun1)} / {thr(f.gun2)} / {thr(f.gun3)})。</>}
          sub={<>色=群分類<GunLegend /></>}>
          {empty ? emptyNote : <PlotFigure options={step1} responsiveWidth />}
        </Step>
        <Step n={2} title="もう一つの軸 — 延数で並べる"
          take={<><b>延数で並べ替えると順位は一変する</b>。延数上位5科のうち{lowGunIn("x")}科が3群以下 —
            ボリュームで病床を支える科は単価の群分類では下位に見える。</>}
          sub="延数を増やす=空床を埋めるがGP改善の第1手 (効率化の順番①)">
          {empty ? emptyNote : <PlotFigure options={step2} responsiveWidth />}
        </Step>
      </div>

      {/* STEP3・STEP4: 積のランキングと医師1人あたり */}
      <div className="grid gap-3 xl:grid-cols-2">
        <Step n={3} title="本来の目的 — GP (=延数×単価) で並べる"
          take={<><b>GP上位5科のうち{lowGunIn("gp")}科は3群以下</b> — 単価だけの群分類は、
            延数でGPを稼ぐ科を過小評価する (「単価◯位」とGP順位の食い違いに注目)。</>}
          sub={<>色=群分類<GunLegend /></>}>
          {empty ? emptyNote : <PlotFigure options={step3} responsiveWidth />}
        </Step>
        <Step n={4} title="医師1人あたりGP — 医師という資源あたりの効率"
          take={<><b>医師1人あたりGPのトップは {drTop3 || "—"}</b>。
            科の規模(医師数)によらない「1人あたりの稼ぎ」で見ると、また別の景色になる。</>}
          sub="分母=常勤換算数 (医療区分・予定緊急によらず合計・期間の月平均)。医師数0の科は除外">
          {empty || drRows.length === 0 ? emptyNote : <PlotFigure options={step4} responsiveWidth />}
        </Step>
      </div>

      {/* STEP5〜8: 2軸散布図 (科別GP分析スライドと同じ系列・順番) */}
      <Step n={5} title="散布図① — 医師人件費 × GP"
        take={<><b>医師人件費が大きい科ほどGPも大きい</b>。橙破線=病院全体の医師人件費比
          {totalJinRatio ? `(${totalJinRatio.toFixed(2)}倍)` : ""} — 破線より上=医師人件費1円あたりのGPが病院平均より高い科。</>}
        sub="・円の大きさ=常勤換算医師数 ・点クリックで注目科 (GP分析タブと共有) ・医師人件費が算出できない科は除外">
        {empty ? emptyNote : (
          <PlotFigure
            options={step5}
            responsiveWidth
            onCellClick={(datum) => {
              const d = datum as { 診療科?: string };
              if (d?.診療科) f.setFocusedDept(f.focusedDept === d.診療科 ? null : d.診療科);
            }}
          />
        )}
      </Step>

      <Step n={6} title={`散布図② — ${vol}延数 × GP`}
        take={<><b>延数とGPは強く連動する — ① 空床を埋める(延数を増やす)がGPを直接押し上げる</b>。
          橙破線=病院全体の1日GP単価 — 破線より上=単価が病院平均より高い科。色(群)が下位でも右上に来る科に注目。</>}
        sub={<>・円の大きさ=常勤換算医師数 ・色=群分類<GunLegend /></>}>
        {empty ? emptyNote : (
          <PlotFigure
            options={step6}
            responsiveWidth
            onCellClick={(datum) => {
              const d = datum as { 診療科?: string };
              if (d?.診療科) f.setFocusedDept(f.focusedDept === d.診療科 ? null : d.診療科);
            }}
          />
        )}
      </Step>

      <Step n={7} title={`散布図③ — 医師1人あたり${vol}延数 × 医師1人あたりGP`}
        take={<><b>医師1人あたりに正規化しても構図は同じ</b> — 右上=1人で延数もGPも稼ぐ科。
          橙破線(病院全体の1日GP単価)より上=単価が平均超。</>}
        sub="・医師数=常勤換算数 (医療区分・予定緊急によらず合計・医師数0の科は除外)">
        {empty || drRows.length === 0 ? emptyNote : (
          <PlotFigure
            options={step7}
            responsiveWidth
            onCellClick={(datum) => {
              const d = datum as { 診療科?: string };
              if (d?.診療科) f.setFocusedDept(f.focusedDept === d.診療科 ? null : d.診療科);
            }}
          />
        )}
      </Step>

      <Step n={8} title={`散布図④ (最後) — 医師1人あたり${vol}延数 × 1日GP単価`}
        take={<><b>最後に単価をy軸へ — ② 1日単価を高める(上へ)の余地を見る</b>。
          緑破線=医師1人あたりGPの等値曲線 (同じ曲線上=医師1人あたりGPが同じ)。
          横帯=現行の群分類 ({thr(f.gun1)} / {thr(f.gun2)} / {thr(f.gun3)}) — 等値曲線を横切る=単価だけでは効率を表せない。</>}
        sub="・y軸=1日GP単価はSTEP1と同じ軸 ・医師数=常勤換算数 (医師数0の科は除外)">
        {empty || drRows.length === 0 ? emptyNote : (
          <PlotFigure
            options={step8}
            responsiveWidth
            onCellClick={(datum) => {
              const d = datum as { 診療科?: string };
              if (d?.診療科) f.setFocusedDept(f.focusedDept === d.診療科 ? null : d.診療科);
            }}
          />
        )}
      </Step>
    </div>
  );
}
